import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ICONS from "../../../../../assets/Icons";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import CommonToast from "../../../../../components/_utils/CommonToast";
import FormatAmount from "../../../../../components/_utils/FormatAmount/FormatAmount";
import RecreatePromptModalPopup from "../../../../../components/_utils/RecreatePromptModalPopup";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { beneficiaryTransactionsReject } from "../../../../../redux/features/transactions/beneficiaryTransactions/index.js";

const BeneficiaryMultiApprovalFailed = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [loadingBox, setLoadingBox] = useState(false);
  const [selectedTxnId, setSelectedTxnId] = useState("");
  const [errorPageData, setErrorPageData] = useState([]);
  const [recreationMode, setRecreationMode] = useState(null);
  const [showRecreateModal, setShowRecreateModal] = useState(false);

  const navigationType = location.state?.type;

  const {
    beneficiaryTransactionsApproveError,
    beneficiaryTransactionsRejectError,
  } = useSelector((state) => state.beneficiaryTransactions);

  const getTransactionsWithErrors = (arr1, arr2) => {
    if (!Array.isArray(arr2)) return arr1;

    const isStringError = arr2.some((item) => typeof item === "string");
    if (isStringError) return arr2;

    const arr2Map = new Map(arr2.map((item) => [item.id, item.errors]));
    return arr1
      .filter((item) => arr2Map.has(item.txn_id))
      .map((item) => ({
        ...item,
        errors: arr2Map.get(item.txn_id),
      }));
  };

  const filterErrorTxn = getTransactionsWithErrors(
    errorPageData,
    JSON.parse(sessionStorage.getItem("beneficiaryTxnError")),
  );

  const getErrorText = (error) => {
    if (typeof error === "string") {
      return error;
    }

    if (error === null || error === undefined) {
      return "Something went wrong";
    }

    if (Array.isArray(error)) {
      return error
        .map((item) => getErrorText(item))
        .filter(Boolean)
        .join(", ");
    }

    if (typeof error === "object") {
      if (error.reason) {
        return getErrorText(error.reason);
      }

      if (error.message) {
        return getErrorText(error.message);
      }

      if (error.errors) {
        return getErrorText(error.errors);
      }

      return JSON.stringify(error);
    }

    return String(error);
  };
  const handleRecreateSingle = (txnId) => {
    setSelectedTxnId(txnId);
    setRecreationMode("single");
    setShowRecreateModal(true);
  };

  const handleRecreateAll = () => {
    setSelectedTxnId("");
    setRecreationMode("all");
    setShowRecreateModal(true);
  };

  const handleRecreationConfirmation = async (reason) => {
    setLoadingBox(true);

    try {
      const transactionIds =
        recreationMode === "all"
          ? errorPageData
              ?.filter((txn) => txn.lifecycle_status !== "rejected")
              .map((txn) => txn.txn_id)
          : [selectedTxnId];

      const response = await dispatch(
        beneficiaryTransactionsReject({ id: transactionIds, reason }),
      );

      if (beneficiaryTransactionsReject.fulfilled.match(response)) {
        CommonToast({
          text: "Transaction rejected and sent for recreation.",
          icon: ICONS?.APPROVE_CIRCLE_GREEN_CHECKMARK,
          variant: "var(--success)",
        });

        if (recreationMode === "all") {
          navigate(
            "/beneficiary_transaction_dashboard/multi_select/multi_approval/declined",
            { state: { type: "recreate_Multi", replace: true } },
          );
        } else {
          const remaining = errorPageData.filter(
            (t) => t.txn_id !== selectedTxnId,
          );
          setErrorPageData(remaining);

          const stored =
            JSON.parse(sessionStorage.getItem("selectedData")) || {};
          sessionStorage.setItem(
            "selectedData",
            JSON.stringify({
              ...stored,
              selectedSendTransactions: remaining,
            }),
          );
        }
      } else {
        CommonToast({
          text: getErrorText(
            response?.payload?.errors?.base || response?.payload?.message,
          ),
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
        });
      }
    } finally {
      setLoadingBox(false);
      setShowRecreateModal(false);
    }
  };

  const buttonRows = [
    [
      {
        btnText: "Send all for recreation",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--negative-bg)",
        icon: ICONS?.RECREATE_WHITE,
        imgColor: "var(--negative-bg)",
        handleFunction: handleRecreateAll,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        hoverOption: "dark",
      },
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--bg-positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS?.DASHBOARD,
        handleFunction: () =>
          navigate("/beneficiary_transaction_dashboard", { replace: true }),
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        hoverOption: "dark",
      },
    ],
  ];

  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    const storedSelectedApproval = JSON.parse(
      sessionStorage.getItem("selectedData"),
    );

    if (storedSelectedApproval) {
      setErrorPageData(storedSelectedApproval?.selectedSendTransactions);
    } else {
      setErrorPageData([]);
    }
  }, []);

  return (
    <>
      {showRecreateModal && (
        <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
          <RecreatePromptModalPopup
            onConfirmBtnClick={handleRecreationConfirmation}
            onCancelBtnClick={() => setShowRecreateModal(false)}
            loadingBox={loadingBox}
            inputDisabled={loadingBox}
          />
        </div>
      )}

      <StatusContainer
        status="error"
        buttonRows={buttonRows}
        title={
          <>
            Oops!&nbsp;
            {beneficiaryTransactionsApproveError
              ? "Transaction approval failed"
              : beneficiaryTransactionsRejectError
                ? "Transaction rejection failed"
                : ""}
          </>
        }
        subtitle={
          navigationType === "recreate_Multi" ? (
            <span className="text-font-xs text-text-primary">
              An error occurred with the following transactions, causing the{" "}
              <br />{" "}
              {beneficiaryTransactionsApproveError ? "approval" : "rejection"}
              &nbsp;of all the transactions to fail.
            </span>
          ) : (
            <span className="text-font-xs text-text-primary">
              Approval of the transactions is not possible due to the following
              errors, their data can be used to recreate new ones.
            </span>
          )
        }
      >
        <>
          <TabsWeapperSection title="Error details">
            <>
              {Array.isArray(filterErrorTxn) &&
                filterErrorTxn.map((val, i) => {
                  if (typeof val === "string") {
                    return (
                      <InfoCard
                        key={i}
                        bgColor="var(--negative-card)"
                        rows={[
                          {
                            leftTitle: val,
                            leftSubTitle: "Error message",
                          },
                        ]}
                      />
                    );
                  }

                  return (
                    <>
                      {val?.errors?.map((error, index) => (
                        <InfoCard
                          key={`${i}-${index}`}
                          bgColor="var(--negative-card)"
                          rows={[
                            {
                              leftTitle: getErrorText(error),
                              leftSubTitle: "Error message",
                            },
                          ]}
                        />
                      ))}
                    </>
                  );
                })}
            </>
          </TabsWeapperSection>

          <TabsWeapperSection title="Transaction Details">
            <>
              {Array.isArray(errorPageData) &&
                errorPageData.map((val, i) => {
                  if (typeof val === "string") return null;

                  return (
                    <InfoCard
                      key={val?.txn_id || i}
                      bgColor="var(--negative-card)"
                      rows={[
                        {
                          leftTitle: val?.beneficiary_account_name,
                          leftSubTitle: "Beneficiary name",
                          rightText: true,
                          rightTitle: (
                            <>
                              INR <FormatAmount price={val?.amount} />
                            </>
                          ),
                          rightTitleTextColor: "var(--text-primary)",
                          rightTextFontWeight: "semibold",
                          rightSubTitle: "Amount",
                        },
                      ]}
                      button={true}
                      btnText="Send for recreation"
                      borderColor="var(--text-primary)"
                      outLineColor="var(--text-primary)"
                      fullWidthRequired={false}
                      enabledBgColor="var(--negative-card)"
                      enabledTextColor="var(--text-primary)"
                      textSemibold={false}
                      handleFunction={() => {
                        handleRecreateSingle(val?.txn_id);
                      }}
                      icon={ICONS?.ARROW_RIGHT_WHITE}
                      imgColor="var(--text-primary)"
                      iconToBeAlign="right"
                      isDisabled={false}
                    />
                  );
                })}
            </>
          </TabsWeapperSection>
        </>
      </StatusContainer>
    </>
  );
};

export default BeneficiaryMultiApprovalFailed;
